import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Icon,
  Badge,
  IconButton,
  Select,
  Button,
  useDisclosure,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Switch,
} from '@chakra-ui/react';
import * as React from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

// Custom components
import Card from 'components/card/Card';
import Menu from 'components/menu/MainMenu';
import {
  MdCheckCircle,
  MdEdit,
  MdOutlineError,
  MdPending,
  MdVisibility,
} from 'react-icons/md';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import Pagination from 'components/pagination';
import { useNavigate } from 'react-router-dom';
import StatusModal from './StatusModal';
import { createAPIEndPoint } from 'config/api/api';
import toast from 'react-hot-toast';
import ErrorMessage from 'components/error-message';
import moment from 'moment';
import axios from 'axios';
import Loader from 'loaders/Loader';
import { logoutUser } from 'utils';

// Column Helper
const columnHelper = createColumnHelper();






// TeamTable Component
export default function StatusTable(props) {

  // Data for Options and Procedure Steps
  const [labData, setLabData] = React.useState([]);
  const [batchData, setBatchData] = React.useState([]);
  const [preAuthData, setPreAuthData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // Fetch lab statuses from the API
  const fetchLabStatus = async () => {
    setLoading(true);
    try {
      const response = await createAPIEndPoint('lab_case_status').fetchAll();
      const labStatuses = response.data.lab_case_statuses || [];
      setLabData(labStatuses);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error fetching lab statuses');
      if (err?.response?.data?.error?.includes('Bearer token')) {
        logoutUser(navigate);
      }
    } finally {
      setLoading(false);
    }
  };



  const fetchBatchStatus = async () => {
    setLoading(true);
    try {
      const response = await createAPIEndPoint('batch_case_status').fetchAll();
      const batchStatuses = response.data.batch_case_statuses || [];
      setBatchData(batchStatuses);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error fetching batch statuses');
      if (err?.response?.data?.error?.includes('Bearer token')) {
        logoutUser(navigate);
      }
    } finally {

      setLoading(false);
    }
  };

  const fetchPreAuthStatus = async () => {
    setLoading(true);
    try {
      const response = await createAPIEndPoint('pre_auth_status').fetchAll();
      const preAuthStatuses = response.data.enabled_statuses || [];
      setPreAuthData(preAuthStatuses);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error fetching pre auth statuses');
      if (err?.response?.data?.error?.includes('Bearer token')) {
        logoutUser(navigate);
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchBatchStatus();
    fetchLabStatus();
    fetchPreAuthStatus();
  }, []);


  // Function to handle the API call
  const handleStatusChange = async (rowIndex, endpoint) => {
    try {
      // Assuming you're sending the request to the API with rowIndex (or any other necessary data)
      const response = await createAPIEndPoint(`${endpoint}/${rowIndex}`).patch()
      console.log('Status updated:', response.data);
      fetchBatchStatus();
      fetchLabStatus();
      fetchPreAuthStatus();      // You may want to update the UI state here (e.g., setState or useQuery)
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };


  // Options Table Columns
  const labColumns = [
    columnHelper.accessor('status_name', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold" >
          Name
        </Text>
      ),
      cell: (info) => <Text color={textColor} fontSize="sm" fontWeight="700" textTransform="capitalize">{info.getValue()}</Text>,
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Status
        </Text>
      ),
      cell: (info) => {
        const role = info.getValue();
        const roleColors = {
          active: 'green',
          inactive: 'red',
        };

        return (
          <Badge
            colorScheme={roleColors[role] || 'gray'}
            px={2}
            py={1.5}
            borderRadius="md"
            textAlign="center"
            minWidth="72px"
          >
            <Text fontSize="sm" textTransform="capitalize" fontWeight="700">
              {role}
            </Text>
          </Badge>
        );
      },
    }),
    columnHelper.accessor('user_name', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          created By
        </Text>
      ),
      cell: (info) => <Text color={textColor} fontSize="sm" fontWeight="700">{info.getValue() || "N/A"}</Text>,
    }),
    columnHelper.accessor('created_at', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          created at
        </Text>
      ),
      cell: (info) => <Text color={textColor} fontSize="sm" fontWeight="700">
        {info.getValue() ? moment(info.getValue()).format('MM/DD/YYYY') : 'N/A'}
      </Text>
    }),
    columnHelper.accessor('status', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Action

        </Text>
      ),
      cell: (info) => <><Switch
        isChecked={info.row.original.status === "active" ? true : false}
        onChange={() => handleStatusChange(info.row.original.id, "lab_case_status")}
        colorScheme="brand"
      /></>,
    }),
  ];

  // Procedure Steps Table Columns
  const batchColumns = [
    columnHelper.accessor('status_name', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Name
        </Text>
      ),
      cell: (info) => <Text color={textColor} fontSize="sm" fontWeight="700" textTransform="capitalize">{info.getValue()}</Text>,
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Status
        </Text>
      ),
      cell: (info) => {
        const role = info.getValue();
        const roleColors = {
          active: 'green',
          inactive: 'red',
        };

        return (
          <Badge
            colorScheme={roleColors[role] || 'gray'}
            px={2}
            py={1.5}
            borderRadius="md"
            textAlign="center"
            minWidth="72px"
          >
            <Text fontSize="sm" textTransform="capitalize" fontWeight="700">
              {role}
            </Text>
          </Badge>
        );
      },
    }),
    columnHelper.accessor('user_name', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          created By
        </Text>
      ),
      cell: (info) => <Text color={textColor} fontSize="sm" fontWeight="700">{info.getValue() || "N/A"}</Text>,
    }),
    columnHelper.accessor('created_at', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          created at
        </Text>
      ),
      cell: (info) => <Text color={textColor} fontSize="sm" fontWeight="700">
        {info.getValue() ? moment(info.getValue()).format('MM/DD/YYYY') : 'N/A'}
      </Text>,
    }),
    columnHelper.accessor('status', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Action
        </Text>
      ),
      cell: (info) => <Switch
        isChecked={info.row.original.status === "active" ? true : false}
        onChange={() => handleStatusChange(info.row.original.id, "batch_case_status")}
        colorScheme="brand"
      />,
    }),
  ];

  // Procedure Steps Table Columns
  const preAuthColumns = [
    columnHelper.accessor('status', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Name
        </Text>
      ),
      cell: (info) => <Text color={textColor} fontSize="sm" fontWeight="700" textTransform="capitalize">{info.getValue()}</Text>,
    }),
    columnHelper.accessor('enabled', {
      id: 'enabled',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Status
        </Text>
      ),
      cell: (info) => {
        const roleColors = info.getValue() === true ? "green" : "red";

        return (
          <Badge
            colorScheme={roleColors || 'gray'}
            px={2}
            py={1.5}
            borderRadius="md"
            textAlign="center"
            minWidth="72px"
          >
            <Text fontSize="sm" textTransform="capitalize" fontWeight="700">
              {info.getValue() === true ? "Active" : "Inactive"}
            </Text>
          </Badge>
        );
      },
    }),
    columnHelper.accessor('createdBy', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          created By
        </Text>
      ),
      cell: (info) => <Text color={textColor} fontSize="sm" fontWeight="700">{info.getValue() || "N/A"}</Text>,
    }),
    columnHelper.accessor('created_at', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          created at
        </Text>
      ),
      cell: (info) => <Text color={textColor} fontSize="sm" fontWeight="700">
        {info.getValue() ? moment(info.getValue()).format('MM/DD/YYYY') : 'N/A'}
      </Text>,
    }),
    columnHelper.accessor('enabled', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Action
        </Text>
      ),
      cell: (info) => <Switch
        isChecked={info.row.original.enabled}
        onChange={() => handleStatusChange(info.row.original.id, "pre_auth_status")}
        colorScheme="brand"
      />,
    }),
  ];

  // React Table instance for Options
  const labStatusTable = useReactTable({
    data: labData,
    columns: labColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // React Table instance for Procedure Steps
  const batchStatusTable = useReactTable({
    data: batchData,
    columns: batchColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // React Table instance for Procedure Steps
  const preAuthStatusTable = useReactTable({
    data: preAuthData,
    columns: preAuthColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });


  const navigate = useNavigate();
  const [type, setType] = React.useState('add');
  const [endpoint, setEndpoint] = React.useState('lab_case');
  const [selectedLocation, setSelectedLocation] = React.useState(null);
  const [sorting, setSorting] = React.useState([]);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');


  const onEditClick = (data) => {
    setType('edit');
    setSelectedLocation(data);
  };

  const onViewClick = (data) => {
    navigate('details');
  };

  const { isOpen, onOpen, onClose } = useDisclosure();




  return (
    <>
      {loading && <Loader />}
      <Card flexDirection="column" w="100%" px="0px" overflowX="auto">
        <Box p={{ base: "0px", md: "25px" }}>
          <Tabs variant="line" colorScheme="brand" isLazy borderRadius="8px" border={{ base: "0px", md: "1px solid #E2E8F0" }}>
            <TabList>
              <Tab py={3.5}
                fontSize={{ base: "sm", md: "md" }}
                fontWeight="bold">Lab Case Status</Tab>
              <Tab py={3.5}
                fontSize={{ base: "sm", md: "md" }}
                fontWeight="bold">Batch Status</Tab>
              <Tab py={3.5}
                fontSize={{ base: "sm", md: "md" }}
                fontWeight="bold">Pre Auth Status</Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <Flex
                  px={{ base: '12px', md: '20px' }}
                  mb="8px"
                  justifyContent="space-between"
                  align="center"
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={{ base: 2, md: 0 }}
                  w="100%"
                >
                  <Text color={textColor} fontSize="20px" fontWeight="700" lineHeight="100%">
                    Lab Case
                  </Text>
                  <Box w={{ base: '100%', md: 'auto' }}>
                    <Button
                      w="100%"
                      // minW={{ base: 'auto', md: '140px' }}
                      variant="brand"
                      fontWeight="500"
                      onClick={() => {
                        onOpen();
                        setEndpoint("lab")
                        setType('add');
                      }}
                      minH={"36px"}
                      borderRadius="16px"
                      mb={0}
                      size="sm"
                    >
                      Add Lab Case Status
                    </Button>
                  </Box>
                </Flex>
                <Box overflowX="auto">
                  <Table variant="striped" color="gray.500" mb="24px" mt="12px">
                    <Thead>
                      {labStatusTable.getHeaderGroups().map((headerGroup) => (
                        <Tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <Th
                              key={header.id}
                              colSpan={header.colSpan}
                              pe="10px"
                              borderColor={borderColor}
                            >
                              <Flex justifyContent="space-between" align="center">
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                              </Flex>
                            </Th>
                          ))}
                        </Tr>
                      ))}
                    </Thead>
                    <Tbody>
                      {labStatusTable.getRowModel().rows.map((row) => (
                        <Tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <Td
                              key={cell.id}
                              fontSize="14px"
                              borderColor="transparent"
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </Td>
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  {!loading &&
                    labData.length === 0 &&
                    <ErrorMessage source="lab case statuses" />
                  }
                </Box>
              </TabPanel>

              <TabPanel>
                <Flex
                  px={{ base: '12px', md: '25px' }}
                  mb="8px"
                  justifyContent="space-between"
                  align="center"
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={{ base: 2, md: 0 }}
                  w="100%"
                >
                  <Text color={textColor} fontSize="20px" fontWeight="700" lineHeight="100%">
                    Batch
                  </Text>
                  <Box w={{ base: '100%', md: 'auto' }}>
                    <Button
                      w="100%"
                      minW={{ base: 'auto', md: '140px' }}
                      variant="brand"
                      fontWeight="500"
                      onClick={() => {
                        onOpen();
                        setEndpoint("batch")
                        setType('add');
                      }}
                    >
                      Add Batch Status
                    </Button>
                  </Box>
                </Flex>
                <Box overflowX="auto">
                  <Table variant="striped" color="gray.500" mb="24px" mt="12px">
                    <Thead>
                      {batchStatusTable
                        .getHeaderGroups()
                        .map((headerGroup) => (
                          <Tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                              <Th
                                key={header.id}
                                colSpan={header.colSpan}
                                pe="10px"
                                borderColor={borderColor}
                              >
                                <Flex
                                  justifyContent="space-between"
                                  align="center"
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                                </Flex>
                              </Th>
                            ))}
                          </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                      {batchStatusTable.getRowModel().rows.map((row) => (
                        <Tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <Td
                              key={cell.id}
                              fontSize="14px"
                              borderColor="transparent"
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </Td>
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </TabPanel>
              <TabPanel>
                <Flex
                  px={{ base: '12px', md: '25px' }}
                  mb="8px"
                  justifyContent="space-between"
                  align="center"
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={{ base: 2, md: 0 }}
                  w="100%"
                >
                  <Text color={textColor} fontSize="20px" fontWeight="700" lineHeight="100%">
                    Pre Auth
                  </Text>
                  <Box w={{ base: '100%', md: 'auto' }}>
                    <Button
                      w="100%"
                      minW={{ base: 'auto', md: '140px' }}
                      variant="brand"
                      fontWeight="500"
                      onClick={() => {
                        onOpen();
                        setEndpoint("pre-auth")
                        setType('add');
                      }}
                    >
                      Add Pre Auth Status
                    </Button>
                  </Box>
                </Flex>
                <Box overflowX="auto">
                  <Table variant="striped" color="gray.500" mb="24px" mt="12px">
                    <Thead>
                      {preAuthStatusTable
                        .getHeaderGroups()
                        .map((headerGroup) => (
                          <Tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                              <Th
                                key={header.id}
                                colSpan={header.colSpan}
                                pe="10px"
                                borderColor={borderColor}
                              >
                                <Flex
                                  justifyContent="space-between"
                                  align="center"
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                                </Flex>
                              </Th>
                            ))}
                          </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                      {preAuthStatusTable.getRowModel().rows.map((row) => (
                        <Tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <Td
                              key={cell.id}
                              fontSize="14px"
                              borderColor="transparent"
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </Td>
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>

      </Card >
      <StatusModal
        isOpen={isOpen}
        onClose={onClose}
        type={type}
        endpoint={endpoint}
        initialData={selectedLocation}
        fetchLab={fetchLabStatus}
        fetchBatch={fetchBatchStatus}
        fetchPreAuth={fetchPreAuthStatus}
      />
    </>
  );
}
